import { Link } from 'react-router-dom';
import { Layers, Plus, Tag, Network, CheckCircle, FolderOpen } from 'lucide-react';
import { AreaDocLayout, DocDivider, ReferenceTable } from '../components/AreaDocLayout';
import { DocSection } from '../components/DocSection';
import { ConceptCard } from '../components/ConceptCard';
import { StepGuide } from '../components/StepGuide';
import { ItemLifecycleDiagram } from '../components/diagrams/ItemLifecycleDiagram';

const relationshipTypes = [
  { rel: 'depends_on', desc: 'This item cannot progress until the target item is complete.', arrow: '→', color: 'text-[var(--coral)]' },
  { rel: 'enables', desc: 'Completing this item creates conditions for the target to succeed.', arrow: '→', color: 'text-[var(--text-ter)]' },
  { rel: 'elaborates', desc: 'This item adds detail or scope to the target (parent/child).', arrow: '→', color: 'text-[var(--text-ter)]' },
  { rel: 'reframes', desc: 'This item is an alternative approach to the same problem.', arrow: '↔', color: 'text-[var(--mustard)]' },
  { rel: 'clusters_with', desc: 'Items belong together thematically but have no execution dependency.', arrow: '↔', color: 'text-[var(--text-ter)]' },
  { rel: 'aggregates', desc: 'This item is the rollup parent of multiple child items.', arrow: '→', color: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
];

export default function ItemManagementDocs() {
  return (
    <AreaDocLayout
      icon={Layers}
      iconBg="bg-primary-50 border-primary-200"
      iconColor="text-primary-600"
      title="Item Management"
      subtitle="Creating, enriching, and managing work items — the always-on foundation of the platform."
      tag="Always-on"
      tagColor="bg-primary-100 text-primary-700"
    >
      {/* Overview */}
      <DocSection
        title="Overview"
        description="Items are the fundamental work objects in Priorities.ai. Unlike cycles or sessions, item management is always-on — no governance container is required."
        accent="green"
      >
        <div className="bg-primary-50 border border-primary-200 p-4 text-sm text-primary-900 leading-relaxed">
          An <strong>Item</strong> is a candidate for prioritization: a feature, initiative, bug, or any unit of work your team evaluates.
          Items exist at the workspace level and are compared to each other only within a <Link to="/prioritization/documentation/tracks" className="underline text-primary-700">Track</Link> inside a <Link to="/prioritization/documentation/cycles" className="underline text-primary-700">Cycle</Link>.
          Enriching items with attributes, relationships, and readiness signals makes them ready for prioritization work.
        </div>
      </DocSection>

      {/* Lifecycle */}
      <DocSection
        title="Item Lifecycle"
        description="Items move through a defined set of states from intake through publication. Governance transitions only happen when items are inside a cycle."
        accent="green"
      >
        <ItemLifecycleDiagram />
      </DocSection>

      <DocDivider />

      {/* Key concepts */}
      <DocSection title="Key Concepts" accent="green">
        <div className="grid gap-3 md:grid-cols-2">
          <ConceptCard
            term="Item Category"
            definition="The type classification that determines which Track Cell an item belongs in (e.g., Initiative, Feature, Bug). Category is stable across cycles."
            icon={Tag}
            color="green"
          />
          <ConceptCard
            term="Altitude"
            definition="The organizational layer — Strategic, Portfolio, or Team — that determines comparability scope. Items at different altitudes are not compared to each other."
            icon={Layers}
            color="green"
          />
          <ConceptCard
            term="Readiness"
            definition="A computed signal that tells you whether an item has the minimum information needed to participate in a prioritization session. Readiness is contextual, not a single status field."
            icon={CheckCircle}
            color="green"
            tag="Computed"
          />
          <ConceptCard
            term="Attributes / Frames"
            definition="Structured scoring dimensions attached to an item — RICE, T-shirt sizing, custom scores. Frames hold the evidence used during sessions."
            icon={Tag}
            color="green"
          />
          <ConceptCard
            term="Relationships"
            definition="Directed or bidirectional links between items: dependencies, elaborations, additional categories, aggregations, clusters. Relationships affect readiness and session results."
            icon={Network}
            color="green"
          />
          <ConceptCard
            term="Collections"
            definition="User-defined groupings of items for planning or review purposes. Collections do not affect ranking or governance."
            icon={FolderOpen}
            color="green"
          />
        </div>
      </DocSection>

      <DocDivider />

      {/* Relationship types */}
      <DocSection
        title="Relationship Types"
        description="Items can be linked to each other with typed relationships. Relationship type determines how items interact during sessions and affect readiness."
        accent="green"
      >
        <div className="border border-[var(--rule)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--surface)] border-b border-[var(--rule)]">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-36">Relationship</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-8">Dir</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading">Description</th>
              </tr>
            </thead>
            <tbody>
              {relationshipTypes.map((r, i) => (
                <tr key={r.rel} className={i % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/50'}>
                  <td className="px-4 py-2.5 font-mono text-xs text-[var(--text-ter)]">{r.rel}</td>
                  <td className={`px-4 py-2.5 text-sm font-bold ${r.color}`}>{r.arrow}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--text-sec)]">{r.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DocSection>

      <DocDivider />

      {/* Common tasks */}
      <DocSection title="Common Tasks" accent="green">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Plus className="h-4 w-4 text-primary-600" />
              <p className="text-sm font-semibold text-[var(--text-pri)] font-heading">Submit an Item</p>
            </div>
            <StepGuide steps={[
              { title: 'Go to Intake → Submit', description: 'Open the Submit form from the left nav or use the quick path from the home dashboard.' },
              { title: 'Fill in name and description', description: 'A clear, actionable title helps reviewers understand the item immediately.' },
              { title: 'Select category and altitude', description: 'Category determines which track cell the item will live in. Altitude sets the comparison scope.' },
              { title: 'Submit', description: 'The item enters the Intake state and appears in the triage queue for review.' },
            ]} />
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="h-4 w-4 text-primary-600" />
              <p className="text-sm font-semibold text-[var(--text-pri)] font-heading">Enrich an Item for Readiness</p>
            </div>
            <StepGuide steps={[
              { title: 'Open the item detail page', description: 'Find the item in the Items list or search for it globally.' },
              { title: 'Add attribute frames', description: 'Fill in scoring dimensions (RICE, sizing, custom attributes) relevant to the session criteria.' },
              { title: 'Set relationships', description: 'Link to parent items, dependencies, or related work to build a complete picture.' },
              { title: 'Check readiness indicator', description: 'The readiness panel shows which signals are complete and which are missing.', note: 'Readiness is re-evaluated automatically as you fill fields.' },
            ]} />
          </div>
        </div>
      </DocSection>

      <DocDivider />

      {/* Reference */}
      <DocSection
        title="Item Field Reference"
        description="Core fields on every item record."
        accent="green"
      >
        <ReferenceTable rows={[
          { field: 'id', type: 'uuid', description: 'Unique item identifier. Stable across all state changes.' },
          { field: 'name', type: 'string', description: 'Display name of the item. Required.' },
          { field: 'description', type: 'string?', description: 'Optional longer description of the work.' },
          { field: 'status', type: 'enum', description: 'Lifecycle state: intake, triage, item work, active, in_cycle, published, deferred, retired.' },
          { field: 'category', type: 'string', description: 'Item category (workspace-defined, e.g. initiative, feature, bug).' },
          { field: 'altitude', type: 'enum', description: 'Comparison scope: strategic, portfolio, team.' },
          { field: 'workspace_id', type: 'uuid', description: 'Workspace this item belongs to.' },
          { field: 'created_at', type: 'timestamp', description: 'When the item was created.' },
          { field: 'updated_at', type: 'timestamp', description: 'When the item was last modified.' },
        ]} />
      </DocSection>
    </AreaDocLayout>
  );
}
